// Epic 10 Showcase — the right-click menu on a collection item. One action:
// add the item to the showcase, or (if it's already there) take it back down.
// The project has no context-menu primitive, so this is a small purpose-built
// one: a fixed-position menu that closes on outside click, Escape, or scroll.

import { useEffect, useRef } from "react";

export interface ShowcaseMenuTarget {
  x: number;
  y: number;
  kind: "certificate" | "sealed_dossier";
  id: string;
  label: string;
  inShowcase: boolean;
}

export function ShowcaseContextMenu({
  target,
  onToggle,
  onClose,
}: {
  target: ShowcaseMenuTarget;
  onToggle: (target: ShowcaseMenuTarget) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Keep the menu on-screen: clamp to the viewport with a small margin.
  const left = Math.min(target.x, window.innerWidth - 200);
  const top = Math.min(target.y, window.innerHeight - 80);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ left, top }}
      className="fixed z-50 min-w-[11rem] overflow-hidden rounded-md border border-brand-gold-500/40 bg-brand-forest-900 py-1 text-brand-paper-100 shadow-lg"
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onToggle(target);
          onClose();
        }}
        className="flex w-full items-center gap-2 px-3 py-2 text-left font-body text-sm hover:bg-brand-forest-800"
      >
        {target.inShowcase ? (
          <>
            <span aria-hidden className="text-state-error-fg">
              ✕
            </span>
            Remove from showcase
          </>
        ) : (
          <>
            <span aria-hidden className="text-brand-gold-300">
              ✦
            </span>
            Showcase
          </>
        )}
      </button>
    </div>
  );
}
